import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { usersApi } from '../../../administration/users/usersApi';
import type { UserListItem } from '../../../administration/users/userTypes';
import { repositoryApi } from '../repositoryApi';
import {
  hasPermission,
  RepositoryPermissions,
  SHARE_COLUMNS,
  withPermission,
  type RepositoryShareSummary,
} from '../repositoryTypes';

/**
 * Who has access to a folder or document, and what each of them may do.
 *
 * **A checkbox MATRIX, captured from GRC's share dialog** — one row per person, one column per
 * permission, and a Save that writes the whole table at once. It replaced an ordered
 * Viewer/Editor/Owner picker on 19 Sep 2026: a rung cannot be rendered as five checkboxes
 * without lying about which of them are independent, and the screen is what the reference
 * supplies.
 *
 * **Nothing is written until Save**, which is what makes Cancel mean something. A reader adds
 * people, ticks and unticks boxes and removes rows, and the dialog holds all of it as a draft.
 *
 * **The flags are what the ITEM allows; the module keys are still the door.** Somebody ticked
 * for Edit who does not hold `repository.update` can open the document and not change it —
 * stated in the footer rather than implied by silence.
 */
export function ShareModal({
  open,
  itemId,
  onClose,
  onSaved,
}: {
  open: boolean;
  itemId: string;
  onClose: () => void;
  /** Fired after a successful save, so the listing re-reads what the caller can now see. */
  onSaved: () => void;
}) {
  const [summary, setSummary] = useState<RepositoryShareSummary | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * The DRAFT table — what the matrix is showing, before Save.
   *
   * Seeded from the server on open and owned by the dialog after that. The creator is excluded:
   * their access is not a grant, so there is nothing here for them to be a row of.
   */
  const [draft, setDraft] = useState<{ userId: string; permissions: number }[]>([]);
  const [cascade, setCascade] = useState(false);
  const [accounts, setAccounts] = useState<UserListItem[]>([]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const next = await repositoryApi.shares(itemId, signal);
        setSummary(next);
        setDraft(
          next.people
            .filter((x) => !x.isCreator)
            .map((x) => ({ userId: x.userId, permissions: x.permissions })),
        );
      } catch (caught) {
        if (signal?.aborted) return;
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not read who has access.', 0),
        );
      }
    },
    [itemId],
  );

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    void load(controller.signal);

    /*
      The account list feeds the search box. It is read-only here and fails SOFT: a reader who
      may share is not thereby a reader of the users module, so a failure leaves the box empty
      and the rest of the dialog working — they can still see the table and remove people.
    */
    void usersApi
      .list({ page: 1, pageSize: 200, sort: 'alias' }, controller.signal)
      .then((page) => setAccounts(page.items))
      .catch(() => {
        /* The search box stays empty; everything else still works. */
      });

    return () => controller.abort();
  }, [open, load]);

  const canManage = summary?.canManage ?? false;
  const creator = summary?.people.find((x) => x.isCreator) ?? null;

  /** Name and email for a row, from whichever list knows them. */
  const labelOf = useMemo(() => {
    const fromServer = new Map(summary?.people.map((x) => [x.userId, x]) ?? []);
    const fromPicker = new Map(accounts.map((x) => [x.id, x]));

    return (userId: string) => {
      const known = fromServer.get(userId);
      if (known) return { name: known.displayName, email: known.email };

      const picked = fromPicker.get(userId);
      return { name: picked?.displayName ?? userId, email: picked?.email ?? null };
    };
  }, [summary, accounts]);

  // Whoever is already in the draft, or is the creator, cannot be added again.
  const taken = new Set([...draft.map((x) => x.userId), ...(creator ? [creator.userId] : [])]);

  function setFlag(userId: string, flag: number, on: boolean) {
    setDraft((rows) =>
      rows.map((row) =>
        row.userId === userId
          ? { ...row, permissions: withPermission(row.permissions, flag, on) }
          : row,
      ),
    );
  }

  async function save() {
    setSaving(true);
    setError(null);

    try {
      await repositoryApi.saveShares(itemId, {
        // A row ticked to nothing is a removal, so it is dropped rather than sent — the server
        // refuses an empty grant, and it would be a rejection the reader could not act on.
        people: draft.filter((x) => x.permissions !== RepositoryPermissions.None),
        applyToChildren: cascade && (summary?.isFolder ?? false),
      });

      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not save sharing.', 0));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={summary ? `Share ${summary.itemName}` : 'Share'}
      size="3xl"
      id="repository-share-modal"
      footer={
        <>
          <Button
            id="repository-share-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          {canManage ? (
            <Button
              id="repository-share-save"
              variant="primary"
              onClick={() => void save()}
              disabled={saving}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {summary === null ? (
          <p className="text-sm text-slate-500">Reading who has access…</p>
        ) : (
          <>
            {/*
              ADD A PERSON — drawn only for somebody who may actually save. A control that
              produced a refusal every time is the dead affordance the hide-rule forbids, and
              `canManage` is resolved server-side from BOTH halves (the Share flag on the item
              and the share key) so the dialog cannot disagree with the endpoint.

              Choosing a name ADDS A ROW rather than committing: the matrix is where the boxes
              are ticked, and a new row starts at View, which is the cautious answer and what a
              share usually means.
            */}
            {canManage ? (
              <Select
                id="repository-share-add"
                ariaLabel="Search users to share with"
                placeholder="Search users to share with…"
                value=""
                onChange={(value) => {
                  if (!value) return;
                  setDraft((rows) => [
                    ...rows,
                    { userId: value, permissions: RepositoryPermissions.View },
                  ]);
                }}
                options={accounts
                  .filter((x) => !taken.has(x.id))
                  .map((x) => ({
                    value: x.id,
                    label: x.displayName,
                    sublabel: x.email ?? undefined,
                  }))}
                error={error?.fieldError('people')}
                disabled={saving}
              />
            ) : null}

            {/*
              THE CASCADE — folders only, because a document has nothing filed under it.

              It is the one control here that writes rows the reader cannot see from where they
              are standing, so its label says what it reaches rather than leaving them to infer
              it from the word "all".
            */}
            {canManage && summary.isFolder ? (
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
                <label
                  htmlFor="repository-share-cascade"
                  className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-800"
                >
                  <Checkbox
                    id="repository-share-cascade"
                    checked={cascade}
                    onChange={(e) => setCascade(e.target.checked)}
                    disabled={saving}
                  />
                  Apply these sharing settings to all files and subfolders inside this folder.
                </label>
              </div>
            ) : null}

            {/* ── THE MATRIX ───────────────────────────────────────────────────────────── */}
            <div className="modern-scrollbar overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full table-auto">
                <thead className="bg-slate-100">
                  <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                    <th className="px-3 py-2 text-left">User</th>
                    {SHARE_COLUMNS.map((column) => (
                      <th
                        key={column.flag}
                        className="border-l border-slate-200 px-3 py-2 text-center"
                      >
                        {column.label}
                      </th>
                    ))}
                    <th className="border-l border-slate-200 px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {/*
                    The CREATOR leads, with every box ticked and none of them changeable. A table
                    built from the stored grants alone would be empty on a private folder and
                    read as "nobody has access", which is the one thing that folder is not.
                  */}
                  {creator ? (
                    <MatrixRow
                      name={creator.displayName}
                      email={creator.email}
                      permissions={RepositoryPermissions.All}
                      locked
                      note="Owner · created it"
                    />
                  ) : null}

                  {draft.map((row) => {
                    const { name, email } = labelOf(row.userId);

                    return (
                      <MatrixRow
                        key={row.userId}
                        name={name}
                        email={email}
                        permissions={row.permissions}
                        locked={!canManage || saving}
                        onToggle={(flag, on) => setFlag(row.userId, flag, on)}
                        onRemove={
                          canManage
                            ? () => setDraft((rows) => rows.filter((x) => x.userId !== row.userId))
                            : undefined
                        }
                      />
                    );
                  })}

                  {/*
                    REACHABLE, and the seeded roots are how: `Uploads/` and `Modules/` are created
                    by the initialiser rather than by a person, so they carry no creator — and a
                    folder with no grants then has nobody to list. An empty grid would read as a
                    dialog that failed to load.
                  */}
                  {creator === null && draft.length === 0 ? (
                    <tr>
                      <td
                        colSpan={SHARE_COLUMNS.length + 2}
                        className="px-3 py-4 text-center text-sm text-slate-500"
                      >
                        Nobody has been given access to this individually. Who can reach it is
                        decided by its general access below.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <GeneralAccess
              summary={summary}
              canManage={canManage}
              disabled={saving}
              itemId={itemId}
              onChanged={() => void load()}
              onError={setError}
            />

            {/*
              The one thing a share CANNOT do, said out loud. A reader who ticks Edit and watches
              the recipient fail to save would otherwise conclude the dialog is broken.
            */}
            <p className="text-xs text-slate-500">
              These boxes decide what this item allows. What somebody can actually do also depends
              on their own repository permissions.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

/** One row of the matrix. */
function MatrixRow({
  name,
  email,
  permissions,
  locked,
  note,
  onToggle,
  onRemove,
}: {
  name: string;
  email: string | null;
  permissions: number;
  /** Ticked and not changeable — the creator, or a reader who may only look. */
  locked?: boolean;
  note?: string;
  onToggle?: (flag: number, on: boolean) => void;
  onRemove?: () => void;
}) {
  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2 align-middle">
        <p className="text-sm text-slate-800">{name}</p>
        {/*
          Suppressed where it IS the name — an account with no alias falls back to its email, and
          printing it on both lines is the same value in two slots.
        */}
        {email && email !== name ? <p className="text-xs text-slate-500">{email}</p> : null}
      </td>

      {SHARE_COLUMNS.map((column) => (
        <td
          key={column.flag}
          className="border-l border-slate-100 px-3 py-2 text-center align-middle"
        >
          <Checkbox
            id={`repository-share--`}
            aria-label={` for `}
            checked={hasPermission(permissions, column.flag)}
            onChange={(e) => onToggle?.(column.flag, e.target.checked)}
            disabled={locked}
          />
        </td>
      ))}

      <td className="border-l border-slate-100 px-3 py-2 text-right align-middle">
        {note ? <span className="text-xs text-slate-500">{note}</span> : null}
        {onRemove ? (
          <button
            type="button"
            className="text-xs font-medium text-red-600 hover:underline"
            onClick={onRemove}
          >
            Remove
          </button>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * GENERAL ACCESS — the "everyone in the organization" half.
 *
 * **Saved on its own, unlike the matrix above it.** It is one value on the folder rather than a
 * table being drafted, and it reaches a different endpoint under the same key — so folding it
 * into Save sharing would make one button write two unrelated things.
 */
function GeneralAccess({
  summary,
  canManage,
  disabled,
  itemId,
  onChanged,
  onError,
}: {
  summary: RepositoryShareSummary;
  canManage: boolean;
  disabled: boolean;
  itemId: string;
  onChanged: () => void;
  onError: (error: ApiError) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function set(permissions: number) {
    setBusy(true);

    try {
      await repositoryApi.setGeneralAccess(itemId, { permissions });
      onChanged();
    } catch (caught) {
      onError(
        caught instanceof ApiError ? caught : new ApiError('Could not change general access.', 0),
      );
    } finally {
      setBusy(false);
    }
  }

  if (summary.publicPermissionsAreInherited) {
    /*
      A FILE takes its folder's, so there is nothing to set here. Stated rather than drawn as a
      disabled control: the reader is not being refused, the value simply does not live on this
      record.
    */
    return (
      <p className="text-sm text-slate-600">
        This document takes its folder&apos;s access
        {summary.publicPermissions === RepositoryPermissions.None
          ? ' — private, so only the people above can reach it.'
          : ' — open to everyone in the organization.'}{' '}
        Change it on the folder.
      </p>
    );
  }

  return (
    <div>
      <h3 className="pb-2 text-sm font-medium text-slate-500">General access</h3>

      {canManage ? (
        <Select
          id="repository-share-general"
          ariaLabel="General access"
          clearable={false}
          sort="none"
          value={String(summary.publicPermissions)}
          onChange={(value) => void set(Number(value))}
          options={GENERAL_ACCESS_OPTIONS}
          description="Who gets access without being named. Sharing is never granted this way — name the people above instead."
          disabled={disabled || busy}
        />
      ) : (
        <p className="text-sm text-slate-600">
          {summary.publicPermissions === RepositoryPermissions.None
            ? 'Private — only the people above can reach it.'
            : 'Open to everyone in the organization.'}
        </p>
      )}
    </div>
  );
}

/**
 * What general access may be set to.
 *
 * **SHARE is never among them.** Handing the whole organization the ability to re-share would
 * make every other control on this dialog decorative, and the server refuses it — so the form
 * does not offer it rather than producing a rejection.
 */
const GENERAL_ACCESS_OPTIONS = [
  {
    value: String(RepositoryPermissions.None),
    label: 'Private',
    sublabel: 'Only you, and the people above.',
  },
  {
    value: String(RepositoryPermissions.View),
    label: 'Anyone in the organization can view',
    sublabel: 'They can open what is in here. Only the people above can change it.',
  },
  {
    value: String(RepositoryPermissions.All & ~RepositoryPermissions.Share),
    label: 'Anyone in the organization can edit',
    sublabel: 'They can also file into it, change it and delete from it.',
  },
];
